import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Button, Chip } from '@mui/material';
import { cockpitAPI } from '../services/api';
import PageTransition from '../components/shared/PageTransition';
import ScrollReveal from '../components/shared/ScrollReveal';
import GlassCard from '../components/shared/GlassCard';
import CockpitKPIStrip from '../components/cockpit/CockpitKPIStrip';
import BrandPerformanceTable from '../components/cockpit/BrandPerformanceTable';
import CockpitCharts from '../components/cockpit/CockpitCharts';
import PeriodSelector from '../components/cockpit/PeriodSelector';
import { colors } from '../styles/colors';

function CockpitPage() {
  const { agency, setSelectedBrand } = useOutletContext();
  const navigate = useNavigate();

  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    // Only show spinner on first load — cache serves instantly after that
    const showSpinner = !data;
    if (showSpinner) setLoading(true);
    setError(null);
    try {
      const res = await cockpitAPI.getSummary(period);
      setData(res.data || res);
    } catch (err) {
      console.error('[CockpitPage] fetch error:', err);
      setError(err?.response?.data?.detail || 'Impossible de charger les donnees du cockpit.');
    } finally {
      if (showSpinner) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBrandClick = (brandId) => {
    setSelectedBrand(brandId);
    navigate('/radar');
  };

  const accentColor = agency?.accent_color || colors.primary;

  // Current month/year in French, capitalized
  const currentMonthYear = (() => {
    const raw = new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  })();

  // --- Loading state ---
  if (loading && !data) {
    return (
      <PageTransition>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress sx={{ color: accentColor }} />
        </Box>
      </PageTransition>
    );
  }

  // --- Error state ---
  if (error && !data) {
    return (
      <PageTransition>
        <Box sx={{ maxWidth: 600, mx: 'auto', mt: 10, px: 3 }}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={fetchData}>
                Reessayer
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 4, md: 5 }, pb: 10 }}>

        {/* ── Page Header ── */}
        <Box sx={{ pt: 5, pb: 4, mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 3 }}>
          <Box>
            <Typography
              sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.15em',
                color: accentColor,
                textTransform: 'uppercase',
                mb: 0.75,
              }}
            >
              {agency?.name || 'Agence'}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontSize: { xs: '3.2rem', sm: '4rem', md: '4.8rem' },
                fontWeight: 400,
                color: colors.charcoal,
                lineHeight: 1.1,
              }}
            >
              Business <em style={{ color: colors.primary, fontStyle: 'italic' }}>Intelligence</em>
            </Typography>
            <Typography
              sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '1rem',
                color: colors.textMed,
                mt: 1,
              }}
            >
              Vue strategique du portefeuille — {currentMonthYear}
            </Typography>
          </Box>
          <PeriodSelector value={period} onChange={setPeriod} accentColor={accentColor} />
        </Box>

        {/* ── KPI Strip ── */}
        <ScrollReveal>
          <CockpitKPIStrip kpis={data?.kpis} accentColor={accentColor} />
        </ScrollReveal>

        {/* ── Table full width ── */}
        <ScrollReveal>
          <BrandPerformanceTable
            brands={data?.brands}
            onBrandClick={handleBrandClick}
            accentColor={accentColor}
          />
        </ScrollReveal>

        {/* ── Reports Directory ── */}
        <ScrollReveal>
          <GlassCard sx={{ p: 4, mt: 3 }}>
            <Typography
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontSize: '1.2rem',
                fontWeight: 700,
                color: colors.charcoal,
                mb: 2.5,
              }}
            >
              Rapports
            </Typography>
            {(data?.reports_directory?.length > 0) ? (
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Marque', 'Frequence', 'Dernier rapport', 'Score', 'Statut', 'Prochain'].map((h) => (
                        <th
                          key={h}
                          style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: '0.72rem',
                            color: colors.textMed,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            fontWeight: 600,
                            padding: '12px 12px',
                            borderBottom: `1px solid ${colors.borderLight}`,
                            textAlign: 'left',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.reports_directory.map((r, rowIndex) => {
                      const freqLabel = r.frequency === 'quotidien' ? 'Quotidien' : r.frequency === 'hebdomadaire' ? 'Hebdomadaire' : 'Mensuel';
                      const statusConfig = {
                        to_generate: { label: 'A generer', color: colors.warning, bg: colors.warningBg },
                        pending_review: { label: 'En attente', color: colors.info, bg: colors.infoBg },
                        sent: { label: 'Envoye', color: colors.success, bg: colors.successBg },
                      };
                      const sc = r.last_report_status ? (statusConfig[r.last_report_status] || statusConfig.sent) : { label: 'Aucun', color: colors.textLight, bg: `${colors.textLight}14` };
                      const isLast = rowIndex === data.reports_directory.length - 1;
                      const tdStyle = {
                        padding: '14px 12px',
                        borderBottom: isLast ? 'none' : `1px solid ${colors.borderLight}`,
                        fontSize: '0.88rem',
                        color: colors.text,
                        verticalAlign: 'middle',
                      };
                      const scoreColor = r.last_report_score != null
                        ? (r.last_report_score >= 80 ? colors.success : r.last_report_score >= 65 ? colors.primary : r.last_report_score >= 50 ? colors.warning : colors.danger)
                        : colors.textLight;

                      return (
                        <tr
                          key={r.brand_id}
                          onClick={() => {
                            setSelectedBrand(r.brand_id);
                            navigate('/mesurer?section=rapports');
                          }}
                          style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = `${colors.primary}08`}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={tdStyle}>
                            <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, color: colors.charcoal, fontSize: '0.88rem' }}>
                              {r.brand_name}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, fontFamily: "'DM Sans', sans-serif", color: colors.textMed }}>
                            {freqLabel}
                          </td>
                          <td style={{ ...tdStyle, fontFamily: "'DM Sans', sans-serif", color: colors.textMed }}>
                            {r.last_report_date
                              ? new Date(r.last_report_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
                              : '—'}
                          </td>
                          <td style={tdStyle}>
                            <span style={{
                              fontFamily: "'Playfair Display', Georgia, serif",
                              fontSize: '0.95rem',
                              fontWeight: 500,
                              color: scoreColor,
                            }}>
                              {r.last_report_score != null ? `${r.last_report_score}/100` : '—'}
                            </span>
                          </td>
                          <td style={tdStyle}>
                            <Chip
                              label={sc.label}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.68rem',
                                fontWeight: 600,
                                fontFamily: '"DM Sans", sans-serif',
                                borderRadius: '100px',
                                bgcolor: sc.bg,
                                color: sc.color,
                              }}
                            />
                          </td>
                          <td style={{ ...tdStyle, fontFamily: "'DM Sans', sans-serif", color: colors.textMed }}>
                            {r.next_report_due
                              ? new Date(r.next_report_due).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
                              : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            ) : (
              <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.88rem', color: colors.textLight }}>
                Aucune marque configuree.
              </Typography>
            )}
          </GlassCard>
        </ScrollReveal>

        {/* ── Charts row (3 side by side) ── */}
        <Box sx={{ mt: 3 }}>
          <ScrollReveal>
            <CockpitCharts charts={data?.charts} accentColor={accentColor} />
          </ScrollReveal>
        </Box>

        {/* ── Footer: Facturation ── */}
        <Box sx={{ mt: 4 }}>
          <ScrollReveal>
            <GlassCard sx={{ p: 4 }}>
              <Typography
                sx={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: colors.charcoal,
                  mb: 2.5,
                }}
              >
                Facturation
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.88rem', color: colors.textMed }}>
                    Cout mensuel total
                  </Typography>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.88rem', fontWeight: 600, color: colors.charcoal }}>
                    {(data?.kpis?.revenue_monthly || 0).toLocaleString('fr-FR')} EUR
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.88rem', color: colors.textMed }}>
                    Marques actives
                  </Typography>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.88rem', fontWeight: 600, color: colors.charcoal }}>
                    {data?.kpis?.brands_count || 0} / {data?.kpis?.brands_max || 10}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.88rem', color: colors.textMed }}>
                    Prochaine facturation
                  </Typography>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.88rem', fontWeight: 600, color: colors.charcoal }}>
                    {(() => {
                      const d = new Date();
                      d.setDate(d.getDate() + 30);
                      return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
                    })()}
                  </Typography>
                </Box>
              </Box>
            </GlassCard>
          </ScrollReveal>
        </Box>

      </Box>

    </PageTransition>
  );
}

export default CockpitPage;
